import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react'
import { DotLoader } from 'react-spinners'
import type { SystemConfig, MaintenancePeriod } from '@common/models/config/system.modl'
import {
  isUnderMaintenance,
  getActiveMaintenancePeriod,
  getSystemConfigVersion,
  isRemoteVersionNewer,
} from '@common/models/config/system.modl'
import { SystemConfigService } from '@common/services/system-config-service'
import { VersionReloadDialog } from '@common/components/VersionReloadDialog'
import MaintenanceScreen from '@/views/error/maintenance'
import packageJson from '../../package.json'

type SystemConfigContextType = {
  systemConfig: SystemConfig | null
  /** 現在日時（メンテナンス判定などに使用、60秒ごとに更新） */
  now: Date
  isMaintenance: boolean
  activeMaintenancePeriod: MaintenancePeriod | null
  /** メンテナンスリストの最後の1件（表示用） */
  lastMaintenancePeriod: MaintenancePeriod | null
  /** 利用規約ページのURL（Firestore の system config） */
  termsOfServiceUrl: string
  isLoading: boolean
  error: Error | null
}

const SystemConfigContext = createContext<SystemConfigContextType>({
  systemConfig: null,
  now: new Date(),
  isMaintenance: false,
  activeMaintenancePeriod: null,
  lastMaintenancePeriod: null,
  termsOfServiceUrl: '',
  isLoading: true,
  error: null,
})

/** メンテナンス判定を時刻の経過で再計算するための間隔（ms） */
const MAINTENANCE_CHECK_INTERVAL_MS = 60_000
const CURRENT_APP_VERSION = packageJson.version ?? '0.0.0'

/** config の取得状態（ログイン画面を出す前に必ず判定するため） */
type ConfigLoadStatus = 'loading' | 'no_doc' | 'loaded' | 'error'

export const SystemConfigProvider = ({ children }: { children: ReactNode }) => {
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null)
  const [loadStatus, setLoadStatus] = useState<ConfigLoadStatus>('loading')
  const [error, setError] = useState<Error | null>(null)
  const [now, setNow] = useState(() => new Date())

  const isLoading = loadStatus === 'loading'

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), MAINTENANCE_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const service = new SystemConfigService()
    const unsubscribe = service.subscribeSystemConfig(
      (config) => {
        setSystemConfig(config)
        setError(null)
        setLoadStatus(config === null ? 'no_doc' : 'loaded')
      },
      (err) => {
        console.error('SystemConfig subscription error:', err)
        setError(err)
        setLoadStatus('error')
      },
    )
    return () => unsubscribe()
  }, [])

  const isMaintenance = useMemo(
    () => (systemConfig ? isUnderMaintenance(systemConfig, now) : false),
    [systemConfig, now]
  )
  const activeMaintenancePeriod = useMemo(
    () => (systemConfig ? getActiveMaintenancePeriod(systemConfig, now) : null),
    [systemConfig, now]
  )
  /** メンテナンスリストの最後の1件（表示用） */
  const lastMaintenancePeriod = useMemo(() => {
    if (!systemConfig?.maintenance?.length) return null
    return systemConfig.maintenance[systemConfig.maintenance.length - 1] ?? null
  }, [systemConfig])
  const termsOfServiceUrl = systemConfig?.termsOfService ?? ''
  const latestAppVersion = useMemo(
    () => (systemConfig ? getSystemConfigVersion(systemConfig, 'school') : null),
    [systemConfig]
  )
  const shouldShowVersionReloadDialog = useMemo(
    () => !!(latestAppVersion && isRemoteVersionNewer(CURRENT_APP_VERSION, latestAppVersion)),
    [latestAppVersion]
  )

  const value: SystemConfigContextType = useMemo(
    () => ({
      systemConfig,
      now,
      isMaintenance,
      activeMaintenancePeriod,
      lastMaintenancePeriod,
      termsOfServiceUrl,
      isLoading,
      error,
    }),
    [
      systemConfig,
      now,
      isMaintenance,
      activeMaintenancePeriod,
      lastMaintenancePeriod,
      termsOfServiceUrl,
      isLoading,
      error,
    ]
  )

  // メンテナンス判定になったらどこにいても強制的にメンテナンス画面を表示。
  // ロード完了までログインを出さない（未ログインでも config 取得 → メンテナンス判定の順で表示）
  const maintenancePeriod = lastMaintenancePeriod ?? activeMaintenancePeriod
  const renderBody = () => {
    if (loadStatus === 'loading') {
      return (
        <div className="loading-overlay">
          <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
        </div>
      )
    }
    // 取得エラー時はログインを出さずメンテナンス画面
    if (loadStatus === 'error') {
      return <MaintenanceScreen activeMaintenancePeriod={null} />
    }
    // config 取得成功後のみメンテナンス判定（loaded でメンテ中ならメンテナンス画面）
    if (loadStatus === 'loaded' && isMaintenance) {
      return <MaintenanceScreen activeMaintenancePeriod={maintenancePeriod} />
    }
    // no_doc または loaded かつ非メンテナンス → 通常のアプリ（ログイン含む）
    return children
  }

  return (
    <SystemConfigContext.Provider value={value}>
      {renderBody()}
      {shouldShowVersionReloadDialog && latestAppVersion ? (
        <VersionReloadDialog
          currentVersion={CURRENT_APP_VERSION}
          latestVersion={latestAppVersion}
        />
      ) : null}
    </SystemConfigContext.Provider>
  )
}

export const useSystemConfig = () => {
  const context = useContext(SystemConfigContext)
  if (!context) {
    throw new Error('useSystemConfig must be used within a SystemConfigProvider')
  }
  return context
}
